import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSessionManager } from "shared/hooks/useAdaptiveLearning";
import { LOADING_MESSAGES, DEFAULT_QUESTIONS_PER_SESSION } from "shared/config/adaptiveLearningConfig";
import QuestionDisplay from "./components/QuestionDisplay";
import QuestionNavigator from "./components/QuestionNavigator";
import Timer from "./components/Timer";

/**
 * Learning Session Page
 * Displays questions and manages the learning session
 */
const LearningSession = ({ 
  studentId: propStudentId, // Accept studentId as prop (integer database ID)
  conceptCode: propConceptCode, 
  conceptProgress: propConceptProgress, 
  onComplete, 
  onBack 
}) => {
  const { conceptCode: paramConceptCode } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [startTime] = useState(Date.now());
  const timerRef = useRef(null);
  const hasAttemptedStart = useRef(false); // Track if we've already tried to start once

  // Use props if provided (StudentHub mode), otherwise use params/state (standalone mode)
  const conceptCode = propConceptCode || paramConceptCode;
  const conceptProgress = propConceptProgress || location.state?.conceptProgress;
  
  // Use studentId from props (integer database ID from the student profile being viewed)
  const studentId = propStudentId;

  const {
    session,
    questions,
    answers,
    currentQuestion,
    currentQuestionIndex,
    timeSpentSeconds,
    loading,
    error,
    status,
    answeredCount,
    allAnswered,
    startSession,
    setAnswer,
    nextQuestion,
    previousQuestion,
    goToQuestion,
    submitSession,
    setTimeSpentSeconds,
  } = useSessionManager(studentId);

  // Start session when studentId is available (only once, never auto-retry)
  useEffect(() => {
    if (studentId && conceptCode && status === "idle" && !hasAttemptedStart.current) {
      hasAttemptedStart.current = true; // Mark that we've attempted to start
      const numberOfQuestions = conceptProgress?.recommendedQuestions || DEFAULT_QUESTIONS_PER_SESSION;
      startSession(conceptCode, numberOfQuestions).catch((err) => {
        console.error("Failed to start session:", err);
      });
    }
  }, [studentId, conceptCode, status, conceptProgress, startSession]);

  // Update timer every second
  useEffect(() => {
    if (status === "ready") {
      timerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        setTimeSpentSeconds(elapsed);
      }, 1000);

      return () => {
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
      };
    }
  }, [status, startTime, setTimeSpentSeconds]);

  // Handle answer selection
  const handleAnswerChange = (questionId, answer) => {
    setAnswer(questionId, answer);
  };

  // Handle submission
  const handleSubmit = async () => {
    try {
      const result = await submitSession();
      // If onComplete prop provided (StudentHub mode), use it
      if (onComplete) {
        // Pass both sessionId and the full result to avoid extra API call
        onComplete(result.id, result, conceptProgress?.progressTier);
      } else {
        // Otherwise use React Router navigation (standalone mode)
        navigate(`/adaptive-learning/results/${result.id}`, {
          state: { session: result, previousTier: conceptProgress?.progressTier },
        });
      }
    } catch (err) {
      console.error("Failed to submit session:", err);
    }
  };

  // Handle exit confirmation
  const handleExit = () => {
    const confirmed = window.confirm(
      "Are you sure you want to exit? Your progress will be lost."
    );
    if (confirmed) {
      // If onBack prop provided (StudentHub mode), use it
      if (onBack) {
        onBack();
      } else {
        // Otherwise use React Router navigation (standalone mode)
        navigate("/adaptive-learning");
      }
    }
  };

  // Loading state
  if (status === "starting" || status === "loading_questions") {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-12 max-w-md text-center">
          <div className="inline-block animate-spin rounded-full h-20 w-20 border-b-4 border-indigo-600 mb-6"></div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
            {status === "starting" ? "Starting Session..." : "Loading Questions..."}
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            {LOADING_MESSAGES.generatingQuestions}
          </p>
          <div className="mt-6">
            <div className="flex items-center justify-center gap-2">
              <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: "0.1s" }}></div>
              <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: "0.2s" }}></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state with retry button
  if (status === "error" || (error && status !== "ready")) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 max-w-md text-center">
          <div className="text-6xl mb-4">😕</div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Failed to Load Questions
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-2">{error}</p>
          
          {/* Additional help text */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 rounded-r-lg text-left mb-6 mt-4">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              <strong>Possible reasons:</strong>
            </p>
            <ul className="text-sm text-blue-600 dark:text-blue-400 mt-2 ml-4 list-disc">
              <li>AI is still generating questions (takes 3-5 seconds)</li>
              <li>Backend service might be temporarily unavailable</li>
              <li>Network connection issue</li>
            </ul>
          </div>
          
          <div className="flex flex-col gap-3">
            <button
              onClick={() => {
                // Retry the session start (manual retry only)
                if (studentId && conceptCode) {
                  const numberOfQuestions = conceptProgress?.recommendedQuestions || DEFAULT_QUESTIONS_PER_SESSION;
                  startSession(conceptCode, numberOfQuestions).catch((err) => {
                    console.error("Retry failed:", err);
                  });
                }
              }}
              disabled={loading}
              className="w-full px-6 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Retrying...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Retry Loading Questions
                </>
              )}
            </button>
            
            <button
              onClick={() => {
                if (onBack) {
                  onBack();
                } else {
                  navigate("/adaptive-learning");
                }
              }}
              className="w-full px-6 py-3 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-medium rounded-lg transition-colors"
            >
              Go Back to Concepts
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Ready state - show questions
  if (status === "ready" && currentQuestion) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 p-4">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 mb-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              {/* Concept Info */}
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                  {session?.conceptName || conceptCode}
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {session?.subject} • Grade {session?.grade}
                </p>
              </div>

              {/* Progress & Timer */}
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                    {answeredCount}/{questions.length}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    Answered
                  </div>
                </div>

                <div className="h-12 w-px bg-gray-300 dark:bg-gray-600"></div>

                <Timer seconds={timeSpentSeconds} />

                <button
                  onClick={handleExit}
                  className="p-2 text-gray-600 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 transition-colors"
                  title="Exit Session"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Question Display */}
          <QuestionDisplay
            question={currentQuestion}
            answer={answers[currentQuestion.questionId]}
            onAnswerChange={(answer) =>
              handleAnswerChange(currentQuestion.questionId, answer)
            }
            questionNumber={currentQuestionIndex + 1}
            totalQuestions={questions.length}
          />

          {/* Navigation */}
          <QuestionNavigator
            currentIndex={currentQuestionIndex}
            totalQuestions={questions.length}
            answeredCount={answeredCount}
            answers={answers}
            questions={questions}
            onPrevious={previousQuestion}
            onNext={nextQuestion}
            onGoToQuestion={goToQuestion}
            onSubmit={handleSubmit}
            allAnswered={allAnswered}
            isSubmitting={loading && status === "submitting"}
          />
        </div>
      </div>
    );
  }

  // Default loading state
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600"></div>
    </div>
  );
};

export default LearningSession;

